/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { selectDilDefinitionByName } from 'srs-middleware';
import { useFacetsState } from 'hooks/useFacetsState';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { HierarchicalFacetDetail } from './hierarchicalFacetDetail';
import { hierarchicalFacets } from 'widgets/endpoints';
import { FacetGroup } from '../../../components/facets/facetGroup';
import { ExcludedFacetsDisplay } from '../../../components/facets/facetItem';
import { facetPreviewCount } from '../facetVars';
import { EmptyFacetsMessage } from 'components/facets/emptyFacetsMessage';

/**
 * This component displays a smaller set of Facets that appears with the other property filters
 *
 * @param {DILProperty} property property to render
 * @param {function} openNewPanel passed down from blueprint PanelStack component. Opens a detailed facet component
 * @param {function} toggleCollapse callback to open/collapse the component
 * @param {boolean} isOpen is the component open or is it collapsed
 */
export const HierarchicalFacetPreview = ({ property, openNewPanel, toggleCollapse, isOpen = false }) => {
	const [filters, setFilters] = useState(null);
	const { getTextFilter, getActiveHierarchicalFacets, toggleExcludeHierarchicalFacet, updateHierarchicalFacets } =
		useFacetsState();
	const textFilter = getTextFilter();
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult, rootResult },
		},
	} = useContext(WorkspaceContext);

	const activeFacetCount = Object.values(getActiveHierarchicalFacets()?.[property.name] || {}).length;
	const totalFacetCount = filters?.fullSize;

	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	// memoize the parameters, so we don't keep fetching on rerender
	/* const parameters = useMemo(() => {
		return rootResult
			? {
					textFilter: textFilter,
					parentResultId: rootResult.resultId,
			  }
			: {};
	}, [rootResult, textFilter]); */

	// fetch the data, given the current parameters
	// if on a "child" result
	// {key: "Label", active: 87, total: 136}
	// if on "root" result
	// {key: "Label", active: 136, total: 0}
	const { data, loading } = useAsyncCallback(async () => {
		if ((activeResult && property && selectedDefinition, rootResult)) {
			if (activeResult.count > 0) {
				const parameters = {
					textFilter: textFilter,
					parentResultId: rootResult.resultId,
				};

				const response = await hierarchicalFacets(activeResult, selectedDefinitionName, {
					propertyList: [property.name],
					parameters: parameters,
				});

				const filters = response[selectedDefinition.systemDefinitionId]?.[property.name];
				return { filters };
			}
		}
	}, [activeResult, property, selectedDefinition, rootResult]);

	// pull out the filters from the return value of the async callback
	// need to keep using the old filters until we new filters so the screen doesn't blink to blank during loading
	useEffect(() => {
		if (!loading) {
			// SRS keys are case insensitive which can cause some problems, transform key to lowercase
			if (data?.filters) {
				data.filters.entries.forEach((entry) => {
					entry.label = entry.key;
					entry.key = entry.key.toLowerCase();
				});
				setFilters(data.filters);
			}
		}
	}, [data, loading]);

	return (
		<FacetGroup
			onResetClick={() => {
				updateHierarchicalFacets(property.name, []);
			}}
			{...{
				property,
				openNewPanel,
				toggleCollapse,
				isOpen,
				loading,
				activeFacetCount,
				totalFacetCount,
				totalRootFacetCount: filters?.entries.length,
			}}
		>
			<ExcludedFacetsDisplay
				excludedFacets={getActiveHierarchicalFacets()}
				toggleFacet={toggleExcludeHierarchicalFacet}
				property={property.name}
			/>

			{filters?.entries.slice(0, facetPreviewCount).map((facet) => {
				return <HierarchicalFacetDetail key={facet.key} facet={facet} property={property} childLimit={3} />;
			})}

			{filters?.entries.length === 0 && <EmptyFacetsMessage />}
		</FacetGroup>
	);
};
